import React, { useRef, useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import {
  Upload, FileSpreadsheet, AlertTriangle, Check, ChevronDown, ChevronRight,
  Loader2, X,
} from "lucide-react";
import { toast } from "sonner";

const fmtEUR = (n) => (n === null || n === undefined || n === "")
  ? "—"
  : new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR" }).format(+n || 0);

const yesNo = (v) => v === false ? "Nee" : "Ja";

/**
 * F17 — Sjablonen Excel-import modal.
 *
 * Flow: kies bestand → strategie → preview (diff) → bevestigen → apply.
 * Blokkeert Apply zolang er errors in de preview zitten.
 */
export default function TemplateImportModal({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [strategy, setStrategy] = useState("add_and_update");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [expanded, setExpanded] = useState({}); // key: `type:idx`, value: bool
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setExpanded({});
    setLoading(false);
  };

  const close = () => { reset(); onClose && onClose(); };

  const toggleExpand = (key) => setExpanded((m) => ({ ...m, [key]: !m[key] }));

  const runPreview = async (chosenFile = file, chosenStrategy = strategy) => {
    if (!chosenFile) return;
    setLoading(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", chosenFile);
      fd.append("strategy", chosenStrategy);
      const token = localStorage.getItem("session_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/templates/import/preview`;
      const resp = await fetch(url, {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        toast.error(data?.detail?.message || data?.detail || "Preview mislukt");
        setPreview(null);
      } else {
        setPreview(await resp.json());
      }
    } catch (e) {
      toast.error("Kon preview niet ophalen");
    } finally {
      setLoading(false);
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setExpanded({});
    // Auto-preview zodra bestand gekozen
    runPreview(f, strategy);
  };

  const onStrategyChange = (v) => {
    setStrategy(v);
    if (file) runPreview(file, v);
  };

  const runApply = async () => {
    if (!file || !preview || preview.errors?.length) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("strategy", strategy);
      const token = localStorage.getItem("session_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/templates/import/apply`;
      const resp = await fetch(url, {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data?.detail?.message || "Import mislukt");
      } else {
        toast.success(`Import voltooid — ${data.added} toegevoegd, ${data.updated} bijgewerkt, ${data.skipped_count || 0} genegeerd.`);
        onImported && onImported(data);
        close();
      }
    } catch (e) {
      toast.error("Import mislukt");
    } finally {
      setLoading(false);
    }
  };

  const hasErrors = !!preview?.errors?.length;
  const canApply = !!preview && !hasErrors && ((preview.new?.length || 0) + (preview.updated?.length || 0)) > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="template-import-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2C2C2A]">
            <FileSpreadsheet className="w-5 h-5 text-[#5D7259]" /> Sjablonen importeren
          </DialogTitle>
          <DialogDescription>
            Kies een .xlsx-bestand van een eerdere export (of dezelfde structuur). Alleen actieve sjablonen worden verwerkt; legacy-sjablonen blijven ongemoeid.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1 — bestand */}
        <div className="space-y-4 mt-2">
          <div className="border-2 border-dashed border-[#E5E2DC] rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={onFileChange}
              data-testid="import-file-input"
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="h-11 rounded-xl border-[#E5E2DC] hover:border-[#D96C4A] gap-2"
              data-testid="import-choose-file-btn"
            >
              <Upload className="w-4 h-4" /> Kies .xlsx
            </Button>
            <div className="text-sm text-[#4F4F4D] flex-1 min-w-0">
              {file ? (
                <span className="font-medium truncate block" data-testid="import-file-name">{file.name}</span>
              ) : (
                <span>Geen bestand gekozen</span>
              )}
            </div>
          </div>

          {/* Step 2 — strategie */}
          {file && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#5D7259]">Importmodus</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { k: "add_only", title: "Alleen toevoegen", desc: "Nieuwe sjablonen worden toegevoegd, bestaande blijven ongewijzigd." },
                  { k: "add_and_update", title: "Toevoegen én bijwerken", desc: "Bestaande actieve sjablonen worden vervangen door de import (volledige regel-replace)." },
                ].map((opt) => (
                  <label
                    key={opt.k}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                      strategy === opt.k
                        ? "border-[#D96C4A] bg-[#FFF7F2]"
                        : "border-[#E5E2DC] bg-white hover:border-[#D96C4A]/40"
                    }`}
                    data-testid={`import-strategy-${opt.k}`}
                  >
                    <input
                      type="radio"
                      name="strategy"
                      className="mt-1"
                      checked={strategy === opt.k}
                      onChange={() => onStrategyChange(opt.k)}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#2C2C2A]">{opt.title}</div>
                      <div className="text-xs text-[#4F4F4D] mt-0.5">{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 3 — preview */}
          {loading && (
            <div className="flex items-center gap-2 text-[#4F4F4D] py-3" data-testid="import-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Preview berekenen…
            </div>
          )}

          {preview && !loading && (
            <div className="space-y-3" data-testid="import-preview">
              {/* Summary chips */}
              <div className="flex flex-wrap gap-2 text-xs font-semibold">
                <span className="px-3 h-7 rounded-full bg-[#EAF4EA] text-[#2E5931] flex items-center gap-1"
                      data-testid="chip-new">
                  <Check className="w-3 h-3" /> {preview.new?.length || 0} nieuw
                </span>
                <span className="px-3 h-7 rounded-full bg-[#FFF3E2] text-[#8A5A1E] flex items-center gap-1"
                      data-testid="chip-updated">
                  {preview.updated?.length || 0} gewijzigd
                </span>
                <span className="px-3 h-7 rounded-full bg-[#F5F1EA] text-[#4F4F4D] flex items-center gap-1"
                      data-testid="chip-skipped">
                  {preview.skipped?.length || 0} genegeerd
                </span>
                {hasErrors && (
                  <span className="px-3 h-7 rounded-full bg-[#FEE7E4] text-[#B4382A] flex items-center gap-1"
                        data-testid="chip-errors">
                    <AlertTriangle className="w-3 h-3" /> {preview.errors.length} fouten
                  </span>
                )}
              </div>

              {/* Errors first — blokkeren apply */}
              {hasErrors && (
                <div className="rounded-xl border border-[#F1B7AD] bg-[#FEF5F2] p-3 space-y-1" data-testid="import-errors">
                  <div className="text-sm font-semibold text-[#B4382A] flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Corrigeer eerst deze fouten:
                  </div>
                  <ul className="text-xs text-[#7F4124] space-y-0.5 list-disc pl-5 max-h-40 overflow-y-auto">
                    {preview.errors.map((e, i) => (
                      <li key={i}><span className="font-semibold">Rij {e.row}</span> — {e.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* New */}
              {preview.new?.length > 0 && (
                <DiffSection title="Nieuwe sjablonen" color="green">
                  {preview.new.map((it, i) => {
                    const key = `new:${i}`;
                    const isOpen = !!expanded[key];
                    return (
                      <ExpandableRow key={key} isOpen={isOpen} onToggle={() => toggleExpand(key)}
                                     testid={`import-new-${i}`}
                                     header={
                                       <>
                                         <span className="font-medium text-[#2C2C2A] truncate">{it.name}</span>
                                         <span className="text-xs text-[#4F4F4D] whitespace-nowrap">
                                           +{it.line_count} regels
                                         </span>
                                       </>
                                     }>
                        <div className="mt-2 space-y-1 text-xs text-[#4F4F4D]">
                          <div><span className="font-semibold">Context:</span> {it.template_context || "—"}</div>
                          <div><span className="font-semibold">Categorieën:</span> {(it.categories || []).join(", ") || "—"}</div>
                          {it.description && <div className="text-[#4F4F4D]">&ldquo;{it.description}&rdquo;</div>}
                          <div className="mt-1 pt-1 border-t border-[#E5E2DC] font-semibold text-[#2C2C2A]">Regels:</div>
                          <ul className="space-y-0.5">
                            {it.lines_preview?.map((L, j) => (
                              <li key={j} className="flex items-baseline gap-2">
                                <span className="font-mono text-[11px] text-[#5D7259]">{L.code}</span>
                                <span className="truncate">{L.description_override || "—"}</span>
                                {L.auto_source && <span className="text-[10px] px-1.5 rounded bg-[#EAF4EA] text-[#2E5931]">auto: {L.auto_source}</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </ExpandableRow>
                    );
                  })}
                </DiffSection>
              )}

              {/* Updated */}
              {preview.updated?.length > 0 && (
                <DiffSection title="Gewijzigde sjablonen" color="amber">
                  {preview.updated.map((it, i) => {
                    const key = `upd:${i}`;
                    const isOpen = !!expanded[key];
                    return (
                      <ExpandableRow key={key} isOpen={isOpen} onToggle={() => toggleExpand(key)}
                                     testid={`import-updated-${i}`}
                                     header={
                                       <>
                                         <span className="font-medium text-[#2C2C2A] truncate">{it.name}</span>
                                         <span className="text-xs text-[#4F4F4D] whitespace-nowrap">
                                           {it.lines_added.length ? `+${it.lines_added.length} ` : ""}
                                           {it.lines_changed.length ? `~${it.lines_changed.length} ` : ""}
                                           {it.lines_removed.length ? `-${it.lines_removed.length}` : ""}
                                         </span>
                                       </>
                                     }>
                        <div className="mt-2 space-y-2 text-xs">
                          {Object.keys(it.head_diffs || {}).length > 0 && (
                            <div>
                              <div className="font-semibold text-[#2C2C2A]">Sjabloon-velden</div>
                              <ul className="pl-3">
                                {Object.entries(it.head_diffs).map(([k, d]) => (
                                  <li key={k}>
                                    <span className="font-mono text-[#5D7259]">{k}:</span>{" "}
                                    <span className="text-[#B4382A]">{String(d.from || "—")}</span>
                                    {" → "}
                                    <span className="text-[#2E5931]">{String(d.to || "—")}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {it.lines_added.length > 0 && (
                            <div>
                              <div className="font-semibold text-[#2E5931]">+ Toegevoegde regels</div>
                              <ul className="pl-3">
                                {it.lines_added.map((L, j) => (
                                  <li key={j}>
                                    <span className="font-mono text-[11px]">{L.code}</span> {L.description_override || ""} {L.unit_price_override != null && <span>({fmtEUR(L.unit_price_override)})</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {it.lines_changed.length > 0 && (
                            <div>
                              <div className="font-semibold text-[#8A5A1E]">~ Gewijzigde regels</div>
                              <ul className="pl-3 space-y-0.5">
                                {it.lines_changed.map((c, j) => (
                                  <li key={j}>
                                    <span className="font-mono text-[11px]">{c.from.code}</span>
                                    {" — "}
                                    {c.fields.map((f) => (
                                      <span key={f} className="ml-1">
                                        <span className="text-[#5D7259]">{f}:</span>{" "}
                                        <span className="text-[#B4382A]">{formatFieldValue(f, c.from[f])}</span>
                                        {" → "}
                                        <span className="text-[#2E5931]">{formatFieldValue(f, c.to[f])}</span>
                                      </span>
                                    ))}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {it.lines_removed.length > 0 && (
                            <div>
                              <div className="font-semibold text-[#B4382A]">− Verdwenen regels</div>
                              <ul className="pl-3">
                                {it.lines_removed.map((L, j) => (
                                  <li key={j}>
                                    <span className="font-mono text-[11px]">{L.code}</span> {L.description_override || ""}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </ExpandableRow>
                    );
                  })}
                </DiffSection>
              )}

              {/* Skipped */}
              {preview.skipped?.length > 0 && (
                <DiffSection title="Genegeerd" color="gray">
                  <ul className="text-xs text-[#4F4F4D] space-y-1" data-testid="import-skipped-list">
                    {preview.skipped.map((s, i) => (
                      <li key={i}>
                        <span className="font-medium text-[#2C2C2A]">{s.name}</span>{" "}
                        <span className="text-[#7A7A78]">— {s.message}</span>
                      </li>
                    ))}
                  </ul>
                </DiffSection>
              )}

              {!preview.new?.length && !preview.updated?.length && !preview.skipped?.length && !hasErrors && (
                <div className="text-sm text-[#4F4F4D] py-2">Geen sjablonen gevonden in het bestand.</div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={close} className="h-11 rounded-xl" data-testid="import-cancel-btn">
            <X className="w-4 h-4 mr-1" /> Annuleren
          </Button>
          <Button
            onClick={runApply}
            disabled={!canApply || loading}
            className="h-11 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] text-white disabled:opacity-50"
            data-testid="import-apply-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
            Import toepassen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiffSection({ title, color, children }) {
  const tone = {
    green: "border-[#CFE3D0] bg-[#F4FAF4]",
    amber: "border-[#F0D7A8] bg-[#FFF9EF]",
    gray:  "border-[#E5E2DC] bg-[#FAF8F4]",
  }[color] || "border-[#E5E2DC] bg-white";
  return (
    <section className={`rounded-xl border ${tone} p-3`}>
      <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#2C2C2A] mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function ExpandableRow({ isOpen, onToggle, header, children, testid }) {
  return (
    <div className="rounded-lg bg-white border border-[#E5E2DC]" data-testid={testid}>
      <button type="button"
              onClick={onToggle}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm">
        {isOpen ? <ChevronDown className="w-4 h-4 text-[#5D7259]" /> : <ChevronRight className="w-4 h-4 text-[#5D7259]" />}
        <div className="flex-1 min-w-0 flex items-baseline gap-2 justify-between">{header}</div>
      </button>
      {isOpen && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

function formatFieldValue(field, val) {
  if (val === null || val === undefined || val === "") return "—";
  if (field === "unit_price_override") return fmtEUR(val);
  if (field === "default_checked") return yesNo(val);
  return String(val);
}
